import { apiGet } from './api';

export interface PaymentFilters {
  dateFrom?: string;
  dateTo?: string;
  status?: 'pending' | 'completed' | 'failed' | 'refunded';
  search?: string;
}

export interface PaginationOptions {
  page: number;
  limit: number;
}

export interface PaymentWithUser {
  id: number;
  amount: number;
  currency: string;
  featureType: 'extra_group_slots' | 'extended_group_size' | 'chat_enhancements';
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  quantity: number;
  paymentProvider?: string | null;
  paymentId?: string | null;
  createdAt: string;
  user: {
    id: number;
    username: string;
    email: string;
  };
}

export interface PaymentsResponse {
  payments: PaymentWithUser[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export class AdminPaymentService {
  /**
   * Get payments with filtering and pagination
   */
  static async getPayments(
    filters: PaymentFilters = {},
    pagination: PaginationOptions = { page: 1, limit: 20 }
  ): Promise<PaymentsResponse> {
    const queryParams = new URLSearchParams();
    
    // Add pagination
    queryParams.append('page', pagination.page.toString());
    queryParams.append('limit', pagination.limit.toString());

    // Add filters
    if (filters.dateFrom) {
      queryParams.append('dateFrom', filters.dateFrom);
    }
    if (filters.dateTo) {
      queryParams.append('dateTo', filters.dateTo);
    }
    if (filters.status) {
      queryParams.append('status', filters.status);
    }
    if (filters.search) {
      queryParams.append('search', filters.search);
    }

    const response = await apiGet<PaymentsResponse>(`/admin/payments?${queryParams}`);
    return response;
  }

  /**
   * Export payments to CSV
   */
  static async exportPayments(filters: PaymentFilters = {}): Promise<void> {
    const queryParams = new URLSearchParams();

    // Add filters
    if (filters.dateFrom) {
      queryParams.append('dateFrom', filters.dateFrom);
    }
    if (filters.dateTo) {
      queryParams.append('dateTo', filters.dateTo);
    }
    if (filters.status) {
      queryParams.append('status', filters.status);
    }
    if (filters.search) {
      queryParams.append('search', filters.search);
    }

    // Get the full URL for the export endpoint
    const baseURL = import.meta.env.VITE_API_URL || '/api';
    const exportUrl = `${baseURL}/admin/payments/export?${queryParams}`;

    try {
      // Auth travels via the httpOnly cookie (credentials: 'include'); the JWT is
      // no longer kept in JS-readable storage, so there is nothing to read here.
      const response = await fetch(exportUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'text/csv',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export payments');
      }

      // Get the filename from the response headers or generate one
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'solofix-payments-export.csv';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1] ?? filename;
        }
      }

      // Get the CSV content
      const csvContent = await response.text();

      // Create a blob and download it
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
      throw new Error('Failed to export payments to CSV');
    }
  }
}